import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import FileUploadField from "@/components/FileUploadField";
import ShareButton from "@/components/ShareButton";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Plus, Send, Trash2, FileDown } from "lucide-react";
import BackButton from "@/components/BackButton";

function StatusBadge({ status }) {
  const map = {
    DRAFT: "bg-secondary text-secondary-foreground",
    PENDING_APPROVAL: "bg-yellow-100 text-yellow-900 border-yellow-200",
    APPROVED: "bg-emerald-100 text-emerald-900 border-emerald-200",
    REJECTED: "bg-red-100 text-red-900 border-red-200",
    ACKNOWLEDGED: "bg-blue-100 text-blue-900 border-blue-200",
    PARTIALLY_RECEIVED: "bg-amber-100 text-amber-900 border-amber-200",
    FULFILLED: "bg-emerald-100 text-emerald-900 border-emerald-200",
  };
  return <Badge variant="outline" className={`${map[status] || ""} font-mono text-[10px]`}>{status}</Badge>;
}

export default function PurchaseOrders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canSeePrice = !!(user?.price_access || user?.role === "ORG_ADMIN" || user?.role === "PROCUREMENT_MANAGER" || user?.role === "PURCHASE_MANAGER" || user?.role === "FINANCE_MANAGER");

  const [items, setItems] = useState([]);
  const [marketplaceVendors, setMarketplaceVendors] = useState([]);
  const [orgVendors, setOrgVendors] = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [approvedPrs, setApprovedPrs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  const buildShareUrl = async (po) => {
    try {
      const { data } = await api.post("/org/documents/share-link", { doc_type: "po", doc_id: po.id });
      const shortUrl = `${window.location.origin}${data.short_path}`;
      return { shortUrl, details: data.details };
    } catch (e) { toast.error(e.response?.data?.detail || "Share link failed"); return null; }
  };

  function blank() { return { vendor_pick: "", vendor_tenant_id: "", vendor_id: "", vendor_name: "", delivery_date: "", invoice_number: "", source_pr_id: "", source_pr_number: "", notes: "", currency: "INR", lines: [emptyLine()] }; }
  function emptyLine() { return { item_id: "", item_name: "", description: "", quantity: 1, unit: "unit", unit_price: 0 }; }

  const load = async () => {
    const [po, mv, ov, im, dp, pr] = await Promise.all([
      api.get("/procurement/purchase-orders"),
      api.get("/marketplace/vendors").catch(() => ({ data: [] })),
      api.get("/org/vendors").catch(() => ({ data: [] })),
      api.get("/inventory/items").catch(() => ({ data: [] })),
      api.get("/org/departments").catch(() => ({ data: [] })),
      api.get("/procurement/requisitions").catch(() => ({ data: [] })),
    ]);
    setItems(po.data); setMarketplaceVendors(mv.data); setOrgVendors(ov.data);
    setItemsMaster(im.data); setDepartments(dp.data);
    setApprovedPrs(pr.data.filter((p) => p.status === "APPROVED"));
  };
  useEffect(() => { load(); }, []);

  const total = form.lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);

  const submit = async () => {
    if (!form.vendor_tenant_id && !form.vendor_name) { toast.error("Vendor is required"); return; }
    try {
      const payload = {
        title: `PO · ${form.vendor_name || "Draft"} · ${new Date().toLocaleDateString()}`,
        vendor_tenant_id: form.vendor_tenant_id || "",
        vendor_id: form.vendor_id || undefined,
        vendor_name: form.vendor_name,
        delivery_date: form.delivery_date,
        invoice_number: form.invoice_number,
        source_pr_id: form.source_pr_id,
        source_pr_number: form.source_pr_number,
        requisition_id: form.source_pr_id,
        notes: form.notes,
        currency: form.currency,
        lines: form.lines.map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })),
      };
      await api.post("/procurement/purchase-orders", payload);
      toast.success("PO created — submit it for approval");
      setOpen(false); setForm(blank()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitForApproval = async (id) => {
    try { const { data } = await api.post(`/procurement/purchase-orders/${id}/submit`); toast.success(`Submitted (${data.status})`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const downloadPoPdf = (po) => {
    const token = (typeof sessionStorage !== "undefined" && sessionStorage.getItem("cera_token")) || localStorage.getItem("cera_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/procurement/purchase-orders/${po.id}/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `po-${po.number}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("Could not download PDF"));
  };

  const updateLine = (idx, k, v) => {
    const lines = [...form.lines]; lines[idx] = { ...lines[idx], [k]: v }; setForm({ ...form, lines });
  };
  const pickItem = (idx, itemId) => {
    const it = itemsMaster.find((x) => x.id === itemId);
    if (!it) return;
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx],
      item_id: itemId, item_name: it.name, unit: it.unit,
      unit_price: it.standard_cost || it.sale_price || 0,
    };
    setForm({ ...form, lines });
  };

  const loadPrLines = async (prId) => {
    if (!prId) return;
    try {
      const { data } = await api.get(`/procurement/requisitions/${prId}`);
      const prLines = (data.lines || []).map((l) => ({
        item_id: l.item_id || "", item_name: l.item_name || "",
        description: l.description || "",
        quantity: Number(l.quantity || 1), unit: l.unit || "unit",
        unit_price: Number(l.estimated_price || 0),
      }));
      if (prLines.length) {
        setForm({ ...form, source_pr_id: prId, source_pr_number: data.number, lines: prLines });
        toast.success(`Loaded ${prLines.length} line${prLines.length === 1 ? "" : "s"} from ${data.number}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load PR"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="po-page">
      <BackButton />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Purchase orders</h1>
          <p className="text-muted-foreground">Issue POs to approved vendors. Multi-level approval based on PO value.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-po-btn"><Plus className="w-4 h-4 mr-1" /> New PO</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-dialog">
            <DialogHeader><DialogTitle>New purchase order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Vendor invoice / reference no.</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="po-invoice-no" /></div>
                <div className="space-y-2"><Label>Delivery date</Label><Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></div>
              </div>
              <div className="space-y-2">
                <Label>Prefill from approved PR (optional)</Label>
                <Select value={form.source_pr_id || ""} onValueChange={loadPrLines}>
                  <SelectTrigger data-testid="po-source-pr"><SelectValue placeholder={approvedPrs.length ? "Pick approved PR to prefill lines" : "No approved PRs yet"} /></SelectTrigger>
                  <SelectContent>
                    {approvedPrs.map((p) => <SelectItem key={p.id} value={p.id}>{p.number} · {p.department || "—"} · {(p.lines || []).length} lines</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.source_pr_number ? <div className="text-xs text-muted-foreground">Linked to <b>{form.source_pr_number}</b></div> : null}
              </div>
              <div className="space-y-2"><Label>Vendor</Label>
                <Select value={form.vendor_pick} onValueChange={(v) => {
                  if (v.startsWith("org:")) {
                    const id = v.slice(4);
                    const ov = orgVendors.find((x) => x.id === id);
                    setForm({ ...form, vendor_pick: v, vendor_tenant_id: "", vendor_id: id, vendor_name: ov?.name || "" });
                  } else if (v.startsWith("mp:")) {
                    const id = v.slice(3);
                    const mv = marketplaceVendors.find((x) => x.id === id);
                    setForm({ ...form, vendor_pick: v, vendor_tenant_id: id, vendor_id: "", vendor_name: mv?.name || "" });
                  }
                }}>
                  <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Pick a vendor">{form.vendor_name || null}</SelectValue></SelectTrigger>
                  <SelectContent>
                    {marketplaceVendors.length > 0 ? <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">Marketplace vendors</div> : null}
                    {marketplaceVendors.map((v) => <SelectItem key={`mp-${v.id}`} value={`mp:${v.id}`}>{v.name}</SelectItem>)}
                    {orgVendors.length > 0 ? <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">My vendor master</div> : null}
                    {orgVendors.map((v) => <SelectItem key={`org-${v.id}`} value={`org:${v.id}`}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.vendor_name ? <div className="text-xs text-muted-foreground">Selected · <b>{form.vendor_name}</b></div> : null}
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

              <div className="border border-border rounded-md">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
                  <div className="label-overline">Line items</div>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })} data-testid="po-add-line-btn">+ Add line</Button>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Qty</TableHead><TableHead>Unit</TableHead>
                    {canSeePrice ? <TableHead>Unit price</TableHead> : null}
                    <TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {form.lines.map((l, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Select value={l.item_id || ""} onValueChange={(v) => pickItem(idx, v)}>
                            <SelectTrigger data-testid={`po-line-item-${idx}`}><SelectValue placeholder="Pick item" /></SelectTrigger>
                            <SelectContent>{itemsMaster.map((it) => <SelectItem key={it.id} value={it.id}>{it.sku} · {it.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="w-20"><Input type="number" value={l.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} data-testid={`po-line-qty-${idx}`} /></TableCell>
                        <TableCell className="w-24"><Input value={l.unit} onChange={(e) => updateLine(idx, "unit", e.target.value)} /></TableCell>
                        {canSeePrice ? (
                          <TableCell className="w-28"><Input type="number" value={l.unit_price} onChange={(e) => updateLine(idx, "unit_price", e.target.value)} data-testid={`po-line-price-${idx}`} /></TableCell>
                        ) : null}
                        <TableCell>{form.lines.length > 1 && (
                          <button onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                        )}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {canSeePrice ? (
                  <div className="px-3 py-2 border-t border-border flex justify-between items-center">
                    <span className="label-overline">Total</span>
                    <span className="font-display font-black">₹{total.toLocaleString()}</span>
                  </div>
                ) : null}
              </div>
              <Button onClick={submit} data-testid="po-create-btn">Create PO</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>PO #</TableHead><TableHead>Vendor invoice</TableHead><TableHead>Vendor</TableHead>
              {canSeePrice ? <TableHead>Total</TableHead> : null}
              <TableHead>Status</TableHead><TableHead>Approval</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`po-row-${i.id}`}>
                <TableCell className="font-mono text-xs">{i.number}</TableCell>
                <TableCell className="font-mono text-xs">{i.invoice_number || "—"}</TableCell>
                <TableCell className="text-sm">{i.vendor_name}</TableCell>
                {canSeePrice ? <TableCell className="font-mono">₹{Number(i.total || 0).toLocaleString()}</TableCell> : null}
                <TableCell><StatusBadge status={i.status} /></TableCell>
                <TableCell className="text-xs font-mono">{i.approval_state?.current_level || 0}/{i.approval_state?.total_levels || 0}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {(i.status === "DRAFT" || i.status === "REJECTED") && (
                      <Button size="sm" variant="outline" onClick={() => submitForApproval(i.id)} data-testid={`po-submit-${i.id}`}><Send className="w-3 h-3 mr-1" /> Submit</Button>
                    )}
                    <button onClick={() => downloadPoPdf(i)} title="Download PO PDF" className="p-1.5 rounded hover:bg-accent" data-testid={`po-pdf-${i.id}`}><FileDown className="w-4 h-4" /></button>
                    <ShareButton
                      docLabel={`${t("common.purchaseOrder")} ${i.number}`}
                      resolveShare={() => buildShareUrl(i)}
                      testId={`po-share-${i.id}`}
                      variant="ghost"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={canSeePrice ? 7 : 6} className="text-center text-muted-foreground py-8">No POs yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
